import type { EventFaq, EventOccurrence, EventRun, EventSnapshot } from '../types'
import type { PlannedSession } from './schedule'

export const newOccurrence = (runId: string, planned: PlannedSession): EventOccurrence => ({
  id: crypto.randomUUID(), event_run_id: runId,
  starts_at: planned.starts_at, ends_at: planned.ends_at,
  is_all_day: false, status: 'published', ticket_url_override: null,
  price_label_override: null, price_min_override: null, source: 'manual',
  source_occurrence_id: `cms:${crypto.randomUUID()}`,
})

// A new place of the event, with no sessions: no suggested date that could be
// published by mistake (PRD.md, item 17).
export const newRun = (eventId: string): EventRun => {
  const id = crypto.randomUUID()
  return {
    id, event_id: eventId, place_id: null, place_name: '', producer_id: null,
    producer_name: '', starts_on: null, ends_on: null,
    is_free: false, requires_ticket: null, price_label: '', price_min: null, ticket_url_source: null,
    ticket_url: null, status: 'published', source: 'manual',
    source_run_id: `cms:${crypto.randomUUID()}`, credits: [], occurrences: [],
  }
}

export const newFaq = (eventId: string, sortOrder: number): EventFaq => ({
  id: crypto.randomUUID(),
  event_id: eventId,
  event_run_id: null,
  question: '',
  answer: '',
  sort_order: sortOrder,
  status: 'published',
})

export const newSnapshot = (category: string): EventSnapshot => {
  const id = crypto.randomUUID()
  return {
    event: {
      id, title: '', description: '', category, producer_id: null, producer_name: '',
      age_min: null, is_kid_friendly: false, image_url_source: null, image_url: null,
      image_focus_x: null, image_focus_y: null, image_page_focus_x: null, image_page_focus_y: null, map_featured: false,
      status: 'published', source: 'manual', source_event_id: `cms:${crypto.randomUUID()}`,
    },
    runs: [newRun(id)],
    faqs: [],
    theme_slugs: [],
    extra_category_slugs: [],
  }
}

// A new event on another's content (PRD.md, item 74): its text, photo, theme,
// a festival's other categories, places with their profiles, prices and
// tickets, and questions, under new ids. Not its sessions, which change from one event to the next, nor its spot on
// the map, its status or its links to the collection. The photo is the one
// already in the Guve, so nothing is fetched again.
export function copyOf(value: EventSnapshot): EventSnapshot {
  const blank = newSnapshot(value.event.category)
  const id = blank.event.id
  const runs = value.runs.filter((run) => run.status !== 'deleted')
  const runIds = new Map(runs.map((run) => [run.id, crypto.randomUUID()]))
  const { event } = value
  return {
    theme_slugs: [...(value.theme_slugs ?? [])],
    extra_category_slugs: [...(value.extra_category_slugs ?? [])],
    event: {
      ...blank.event,
      title: `Cópia de ${event.title}`, description: event.description,
      producer_id: event.producer_id, producer_name: event.producer_name,
      age_min: event.age_min, is_kid_friendly: event.is_kid_friendly,
      image_url: event.image_url, image_focus_x: event.image_focus_x, image_focus_y: event.image_focus_y,
      image_page_focus_x: event.image_page_focus_x ?? null, image_page_focus_y: event.image_page_focus_y ?? null,
    },
    runs: runs.length ? runs.map((run) => ({
      ...newRun(id),
      id: runIds.get(run.id)!,
      place_id: run.place_id, place_name: run.place_name, producer_id: run.producer_id, producer_name: run.producer_name,
      credits: [...(run.credits ?? [])], is_free: run.is_free, requires_ticket: run.requires_ticket ?? null,
      price_label: run.price_label, price_min: run.price_min,
      ticket_url_source: run.ticket_url_source, ticket_url: run.ticket_url_source,
    })) : blank.runs,
    faqs: value.faqs
      .filter((faq) => faq.status !== 'deleted' && (!faq.event_run_id || runIds.has(faq.event_run_id)))
      .map((faq, index) => ({
        ...newFaq(id, index),
        event_run_id: faq.event_run_id ? runIds.get(faq.event_run_id)! : null,
        question: faq.question, answer: faq.answer,
      })),
  }
}

// The ticket link as the team typed it: the one the app opens, without the
// utm_source=guve that cms-events adds on saving (PRD.md, item 15).
export const plainTicketUrl = (link: string | null) => {
  if (!link) return null
  try {
    const url = new URL(link)
    if (url.searchParams.get('utm_source') === 'guve') url.searchParams.delete('utm_source')
    return url.toString()
  } catch {
    return link
  }
}

// The sessions in time order, new ones included.
export const sortOccurrences = (occurrences: EventOccurrence[]) =>
  [...occurrences].sort((left, right) => (left.starts_at || '').localeCompare(right.starts_at || ''))

// What the editor controls in each part of an event. The CMS compares them with
// the event as it opened and flags what stayed the same, which the database
// then leaves untouched (PRD.md, item 1): a collection that ran meanwhile keeps
// what it brought, and only a real edit is marked as one.
const EVENT_FIELDS = ['title', 'description', 'category', 'producer_id', 'producer_name', 'age_min', 'is_kid_friendly',
  'image_url_source', 'image_url', 'image_focus_x', 'image_focus_y', 'image_page_focus_x', 'image_page_focus_y',
  'map_featured', 'status'] as const
const RUN_FIELDS = ['place_id', 'place_name', 'producer_id', 'producer_name', 'is_free', 'requires_ticket',
  'price_label', 'price_min', 'ticket_url_source', 'ticket_url', 'status'] as const
const OCCURRENCE_FIELDS = ['starts_at', 'ends_at', 'is_all_day', 'status', 'ticket_url_override',
  'price_label_override', 'price_min_override'] as const

const same = <T extends object>(left: T, right: T, fields: readonly (keyof T)[]) =>
  fields.every((field) => (left[field] ?? null) === (right[field] ?? null))

export function flagUnchanged(current: EventSnapshot, original: EventSnapshot | null): EventSnapshot {
  if (!original) return current
  const runsBefore = new Map(original.runs.map((run) => [run.id, run]))
  return {
    ...current,
    themes_unchanged: original.theme_slugs !== undefined && current.theme_slugs !== undefined
      && JSON.stringify([...current.theme_slugs].sort()) === JSON.stringify([...original.theme_slugs].sort()),
    event: { ...current.event, unchanged: same(current.event, original.event, EVENT_FIELDS) },
    runs: current.runs.map((run) => {
      const before = runsBefore.get(run.id)
      if (!before) return run
      const occurrencesBefore = new Map(before.occurrences.map((item) => [item.id, item]))
      const sameCredits = JSON.stringify(run.credits ?? []) === JSON.stringify(before.credits ?? [])
      const { credits, ...rest } = run
      return {
        ...rest,
        // Credits left as they were are not sent, so they are not rewritten.
        ...(sameCredits ? {} : { credits }),
        unchanged: same(run, before, RUN_FIELDS),
        occurrences: run.occurrences.map((item) => {
          const previous = occurrencesBefore.get(item.id)
          return previous ? { ...item, unchanged: same(item, previous, OCCURRENCE_FIELDS) } : item
        }),
      }
    }),
  }
}
